/**
 * Riepilogo mensile della fatturazione, pensato per l'allineamento con
 * Fatture in Cloud senza doppia trascrizione:
 *
 *  - ogni riga ha una spunta: spuntata = fattura emessa davvero;
 *  - togliere la spunta (contestazione, variazione richiesta dal cliente)
 *    riporta la fattura fra le "da emettere" alla prima scadenza utile;
 *  - le commesse a fatturazione periodica compaiono come PROPOSTE con
 *    l'importo dell'ultima rata: un clic le trasforma in fatture emesse
 *    scalando il residuo della commessa.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fatturazionIApi } from '../api';
import SelettoreMese, { meseDiLavoro, nomeMese } from './SelettoreMese';
import SceltaMese from './SceltaMese';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
// Solo mese e anno: la competenza è un mese, non un giorno.
const mesiBrevi = d => d
  ? new Date(d).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })
  : '—';

export default function RiepilogoMensile({ onCambiato, onVaiARiaddebiti }) {
  const adesso = new Date();
  // Si apre sul mese che si sta fatturando davvero, non su quello del calendario.
  const iniziale = meseDiLavoro();
  const [anno, setAnno] = useState(iniziale.anno);
  const [mese, setMese] = useState(iniziale.mese);
  const [dati, setDati] = useState(null);
  const [lavorando, setLavorando] = useState(false);
  const [importi, setImporti] = useState({});   // override importo per le proposte
  // Spese anticipate e rendiconti aperti: non sono fatture, ma chi fattura
  // deve sapere che esistono prima di chiudere il mese.
  const [inSospeso, setInSospeso] = useState(null);
  // Importi corretti a mano prima di emettere: il valore proposto da un SAL o
  // da una tranche non è sempre quello che si fattura davvero.
  const [modificati, setModificati] = useState({});

  const carica = () => fatturazionIApi.riepilogo(anno, mese).then(d => { setDati(d); setImporti({}); }).catch(console.error);
  useEffect(() => { carica(); }, [anno, mese]);
  useEffect(() => { fatturazionIApi.daRiaddebitare().then(setInSospeso).catch(() => setInSospeso(null)); }, []);

  const dopo = () => { carica(); onCambiato?.(); };

  const commuta = async (r) => {
    const emessaOra = ['emessa', 'pagata'].includes(r.stato);
    if (emessaOra && !confirm(
      `Togliere la spunta a "${r.descrizione}" (${fmt(r.importo)})?\n\nLa fattura tornerà fra le DA EMETTERE alla prima scadenza utile.`)) return;
    setLavorando(true);
    try { await fatturazionIApi.spunta(r.id, !emessaOra); dopo(); }
    catch (e) { alert(e); } finally { setLavorando(false); }
  };

  /** Salva l'importo corretto di una fattura ancora da emettere. */
  const salvaImporto = async (r, valore) => {
    const nuovo = Number(String(valore).replace(',', '.'));
    if (!Number.isFinite(nuovo) || Math.abs(nuovo - Number(r.importo)) < 0.005) {
      setModificati(m => { const { [r.id]: _, ...resto } = m; return resto; });
      return;
    }
    setLavorando(true);
    try {
      await fatturazionIApi.update(r.id, {
        commessa_id: r.commessa_id, numero_fattura: r.numero_fattura, descrizione: r.descrizione,
        importo: nuovo, data_emissione: r.data_emissione, data_scadenza: r.data_scadenza,
        data_pagamento: r.data_pagamento, stato: r.stato, note: r.note,
        competenza: r.competenza ? String(r.competenza).slice(0, 7) : '',
        ordine_id: r.ordine_id,
      });
      setModificati(m => { const { [r.id]: _, ...resto } = m; return resto; });
      dopo();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  /**
   * Sposta una fattura non ancora emessa in un altro mese di competenza.
   *
   * Si faceva solo dalla scheda della fattura, in un'altra pagina: ma il mese
   * sbagliato lo si scopre proprio qui, guardando il riepilogo.
   */
  const spostaMese = async (r, nuovo) => {
    if (!/^\d{4}-\d{2}$/.test(nuovo || '')) return;
    const attuale = r.competenza ? String(r.competenza).slice(0, 7) : null;
    if (nuovo === attuale) return;
    const [a2, m2] = nuovo.split('-').map(Number);
    if (!confirm(`Spostare "${r.descrizione}" (${fmt(r.importo)}) a ${nomeMese(m2)} ${a2}?\n\n`
      + 'Da qui in avanti comparirà nel riepilogo di quel mese, non più in questo.')) return;
    setLavorando(true);
    try {
      await fatturazionIApi.update(r.id, {
        commessa_id: r.commessa_id, numero_fattura: r.numero_fattura, descrizione: r.descrizione,
        importo: r.importo, data_emissione: r.data_emissione, data_scadenza: r.data_scadenza,
        data_pagamento: r.data_pagamento, stato: r.stato, note: r.note,
        competenza: nuovo, ordine_id: r.ordine_id,
      });
      dopo();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  const emettiProposta = async (p) => {
    const imp = Number(importi[p.commessa_id] ?? p.importo_proposto);
    if (!(imp > 0)) return alert('Indica un importo');
    setLavorando(true);
    try {
      // La competenza è il mese che si sta guardando, non quello in cui si
      // clicca: è il senso stesso di fatturare agosto a settembre.
      await fatturazionIApi.emettiPeriodica({
        commessa_id: p.commessa_id, importo: imp,
        competenza: `${anno}-${String(mese).padStart(2, '0')}-01`,
        // La rata si aggancia all'ordine attivo della commessa, se c'è
        ordine_id: p.ordine_id || null,
      });
      dopo();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  if (!dati) return <div className="text-sm text-gray-400 py-8 text-center">Caricamento…</div>;

  const righe = dati.righe || [];
  const proposte = dati.proposte || [];
  // Le proposte già "coperte" da una tranche da_emettere della stessa commessa
  // nel mese non vanno mostrate due volte
  const commesseInRighe = new Set(righe.filter(r => r.stato === 'da_emettere').map(r => r.commessa_id));
  const proposteVisibili = proposte.filter(p => !commesseInRighe.has(p.commessa_id));

  const emesse = righe.filter(r => ['emessa', 'pagata'].includes(r.stato));
  const totEmesso = emesse.reduce((s, r) => s + Number(r.importo), 0);
  const totPrevisto = righe.filter(r => r.stato === 'da_emettere').reduce((s, r) => s + Number(r.importo), 0)
    + proposteVisibili.reduce((s, p) => s + Number(importi[p.commessa_id] ?? p.importo_proposto), 0);

  const inRitardo = (p) => new Date(p.data_prevista) < new Date(anno, mese - 1, 1);

  return (
    <div className="space-y-4">
      <SelettoreMese anno={anno} mese={mese}
                     onChange={(a, m) => { setAnno(a); setMese(m); }}>
        <div className="ml-auto text-sm text-gray-600 flex gap-4">
          <span>Emesso: <strong className="text-green-700">{fmt(totEmesso)}</strong></span>
          <span>Da emettere: <strong className="text-amber-700">{fmt(totPrevisto)}</strong></span>
        </div>
      </SelettoreMese>

      {/* Spese anticipate da riaddebitare e rendiconti ancora aperti: entrano
          in fattura solo approvando il rendiconto, quindi vanno viste adesso. */}
      {inSospeso && (inSospeso.totale_spese > 0 || inSospeso.commesse.length > 0) && (
        <div className="card bg-amber-50/60 border-amber-200 py-2.5 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px] text-sm text-amber-900">
            {inSospeso.totale_spese > 0 && (
              <>Ci sono <strong>{fmt(inSospeso.totale_spese)}</strong> di spese anticipate da
                riaddebitare{inSospeso.commesse.length > 1 ? ' su ' : ' su '}
                {inSospeso.commesse.length} {inSospeso.commesse.length === 1 ? 'commessa' : 'commesse'}.</>
            )}
            {inSospeso.totale_spese === 0 && (
              <>Ci sono rendiconti aperti su {inSospeso.commesse.length}{' '}
                {inSospeso.commesse.length === 1 ? 'commessa' : 'commesse'}.</>
            )}
            <div className="text-xs text-amber-800/80 mt-0.5">
              Diventano fattura quando approvi il rendiconto che le contiene.
            </div>
          </div>
          {onVaiARiaddebiti && (
            <button className="btn-secondary btn-sm shrink-0" onClick={onVaiARiaddebiti}>
              Vedi spese e rendiconti
            </button>
          )}
        </div>
      )}

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-3 py-3 w-10" title="Spuntata = emessa davvero">✓</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Competenza</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Commessa</th>
              <th className="px-4 py-3">Descrizione</th>
              <th className="px-4 py-3 text-right">Importo</th>
              <th className="px-4 py-3">N. fattura</th>
            </tr>
          </thead>
          <tbody>
            {righe.length === 0 && proposteVisibili.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                Nessuna fattura emessa o prevista in {nomeMese(mese)} {anno}
              </td></tr>
            )}

            {righe.map(r => {
              const spuntata = ['emessa', 'pagata'].includes(r.stato);
              return (
                <tr key={r.id} className={`border-b last:border-0 ${spuntata ? 'bg-green-50/50' : 'hover:bg-gray-50'}`}>
                  <td className="px-3 py-3">
                    <input type="checkbox" className="w-4 h-4 cursor-pointer" checked={spuntata}
                           disabled={lavorando} onChange={() => commuta(r)}
                           title={spuntata ? 'Togli la spunta: torna fra le da emettere' : 'Segna come emessa oggi'} />
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {spuntata ? fmtDate(r.data_emissione) : <>scad. {fmtDate(r.data_scadenza)}</>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {spuntata ? (
                      <span className={r.competenza_dedotta ? 'text-gray-400 italic' : 'text-gray-700'}
                            title={r.competenza_dedotta
                              ? 'Competenza dedotta dalla data di emissione'
                              : 'Competenza impostata'}>
                        {mesiBrevi(r.competenza_effettiva)}{r.competenza_dedotta ? ' ?' : ''}
                      </span>
                    ) : (
                      // Non ancora emessa: il mese si cambia da qui, dove ci si
                      // accorge che è quello sbagliato.
                      <SceltaMese className="input py-0.5 pl-1.5 pr-5 text-xs min-w-[164px]"
                                  disabled={lavorando}
                                  title={r.competenza_dedotta
                                    ? 'Mese dedotto dalla scadenza: scegline uno per fissarlo'
                                    : 'Mese di competenza: cambialo per spostare la fattura'}
                                  value={String(r.competenza_effettiva || '').slice(0, 7)}
                                  onChange={v => spostaMese(r, v)} />
                    )}
                    {/* Dovuta in un mese precedente e non ancora emessa: resta qui
                        finché non la si spunta, ma deve vedersi che è indietro. */}
                    {r.arretrata && (
                      <span className="block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 w-fit">
                        arretrata
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{r.cliente_nome || '—'}</td>
                  <td className="px-4 py-3">
                    {r.commessa_id
                      ? <Link className="font-mono text-xs text-brand-600 hover:underline" to={`/commesse/${r.commessa_id}`}>{r.commessa_numero}</Link>
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-[280px] truncate">
                    {r.tipo_documento === 'nota_credito' && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 mr-1.5">
                        NOTA DI CREDITO
                      </span>
                    )}
                    {r.descrizione}
                    {/* Ordine del cliente: il numero da riportare in fattura,
                        e l'avviso se manca o se l'ordine è ormai saturo. */}
                    {r.ordine_numero && (
                      <span className="block text-[11px] text-gray-400 font-mono"
                            title={r.ordine_riferimento || "Ordine del cliente da citare in fattura"}>
                        ordine {r.ordine_numero}{r.ordine_sforato && ' · oltre il valore ordinato'}
                      </span>
                    )}
                    {r.ordine_mancante && (
                      <span className="block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 w-fit"
                            title="La commessa ha un ordine del cliente: collega la fattura dalla scheda commessa">
                        senza ordine
                      </span>
                    )}
                  </td>
                  {/* Finché non è emessa l'importo si corregge qui: quello
                      proposto dal rendiconto o dalla tranche non è sempre
                      quello che si fattura. */}
                  <td className={`px-4 py-3 text-right font-medium ${Number(r.importo) < 0 ? 'text-red-600' : ''}`}>
                    {spuntata ? fmt(r.importo) : (
                      <input className="input py-0.5 text-sm text-right w-28 inline-block"
                             value={modificati[r.id] ?? Number(r.importo)}
                             disabled={lavorando}
                             onChange={e => setModificati(m => ({ ...m, [r.id]: e.target.value }))}
                             onBlur={e => salvaImporto(r, e.target.value)}
                             onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                             title="Correggi l'importo e premi Invio: vale fino a quando non spunti la fattura" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{r.numero_fattura || '—'}</td>
                </tr>
              );
            })}

            {/* Proposte dalle commesse periodiche */}
            {proposteVisibili.map(p => (
              <tr key={`p${p.commessa_id}`} className={`border-b last:border-0 border-dashed ${inRitardo(p) ? 'bg-red-50/60' : 'bg-amber-50/40'}`}>
                <td className="px-3 py-3">
                  <button className="btn-sm btn bg-brand-600 text-white hover:bg-brand-700 px-1.5"
                          disabled={lavorando} title="Emetti questa fattura periodica"
                          onClick={() => emettiProposta(p)}>✓</button>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className={inRitardo(p) ? 'text-red-600 font-semibold' : 'text-amber-700'}>
                    prevista {fmtDate(p.data_prevista)}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-700">{nomeMese(mese).slice(0, 3)} {anno}</td>
                <td className="px-4 py-3 text-gray-700">{p.cliente_nome}</td>
                <td className="px-4 py-3">
                  <Link className="font-mono text-xs text-brand-600 hover:underline" to={`/commesse/${p.commessa_id}`}>{p.commessa_numero}</Link>
                </td>
                <td className="px-4 py-3 text-gray-500 max-w-[280px] truncate italic">
                  Rata periodica — {p.commessa_titolo}
                  <span className="not-italic text-xs text-gray-400"> · residuo {fmt(p.residuo)}</span>
                  {p.ordine_numero && (
                    <span className="block not-italic text-[11px] text-gray-400 font-mono">ordine {p.ordine_numero}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <input className="input py-0.5 text-sm text-right w-24 inline-block" type="number" step="0.01"
                         value={importi[p.commessa_id] ?? Number(p.importo_proposto)}
                         onChange={e => setImporti(x => ({ ...x, [p.commessa_id]: e.target.value }))} />
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">proposta</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Spunta = fattura emessa davvero (in Fatture in Cloud). Togliendo la spunta la fattura torna
        fra le da emettere alla prima scadenza utile. Le righe tratteggiate sono le rate periodiche
        proposte dal sistema: correggi l'importo se serve e conferma con ✓.
        Nella colonna <strong>Competenza</strong>, finché la fattura non è emessa, il mese si cambia
        da lì: scegliendone un altro la fattura si sposta in quel riepilogo.
      </p>
    </div>
  );
}
